import { useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import Popover from '@ui/popover/popover';
import Button from '@ui/buttons/button/button';
import Container from '@ui/container/container';
import Icon from '@ui/icons/icon';
import { ICONS } from '@ui/icons/types';
import Pill from '@ui/pill/pill';
import RotatableIcon from '@ui/RotatableIcon/rotatable-icon';
import Text from '@ui/text/text/text';
import { useAppSelector } from '@/store/hooks';
import { selectProjectName, selectWorkingProjectUuid } from '@/store/slices/project/selectors';
import { ScheduledEmailStatus } from '@/types/email.types';
import type { UserStatus } from '@/types/all-attendees.types';
import { scheduledEmailToCampaign } from '@/utils/scheduled-email-campaign';
import useLanguageParams from '@/utils/use-language-params';
import globalWindowSize from '@/utils/global-window-size';
import { getCampaignStatusColor, getCampaignStatusLabel } from '../../../campaigns/utils';
import useLoadEventPublishedScheduledEmails from '../../../campaigns/use-load-event-published-scheduled-emails';
import classes from './campaign-usage-dropdown.module.css';

const PANEL_WIDTH = 360;
const PANEL_MAX_HEIGHT = 400;
const TRIGGER_GAP = 8;
const WINDOW_EDGE = 16;

type Props =
  /** a dynamic list - matched against each campaign's dynamic_audiences */
  | { status: UserStatus; passcodeListUuid?: never }
  /** an audience (passcode) list - matched against each campaign's audiences */
  | { passcodeListUuid: string | undefined; status?: never };

/** Shows which of the event's campaigns send to this audience list. Renders nothing when none do. */
const CampaignUsageDropdown = ({ status, passcodeListUuid }: Props) => {
  const projectUuid = useAppSelector(selectWorkingProjectUuid);
  const projectName = useAppSelector(selectProjectName);
  // the published version of each campaign - unpublished draft changes to its audiences don't count
  const { publishedEmails } = useLoadEventPublishedScheduledEmails();
  const language = useLanguageParams();
  // the list views clip their overflow, so the panel is portaled out and positioned from the trigger
  const popoverPortal = useRef<HTMLDivElement | null>(null);

  const campaigns = useMemo(
    () =>
      (publishedEmails ?? [])
        .filter(
          (email) =>
            email.status !== ScheduledEmailStatus.deleted &&
            (status
              ? email.dynamic_audiences?.includes(status)
              : !!passcodeListUuid && email.audiences?.some((audience) => audience.value === passcodeListUuid))
        )
        .map(scheduledEmailToCampaign),
    [publishedEmails, status, passcodeListUuid]
  );

  if (!campaigns.length) return null;

  return (
    <>
      <div className={classes.divider} />
      <Popover
        id="campaign-usage-dropdown"
        portalEl={popoverPortal}
        cycleWithArrowKeys
        refocusTriggerOnClose
        renderTriggerButton={({ open, closing }) => (
          <Button
            clear
            padding="0 16px"
            borderRadius="100px"
            className="flex ai-center gap-8"
            designSize="small"
            border={`1px solid var(--global-ico-text-${open ? 1 : 3})`}
            hoverBorder="1px solid var(--global-ico-text-1)"
          >
            <Icon name={ICONS.LINK} size={12} color="var(--global-ico-text-3)" />
            <Text className="P2" whiteSpace="nowrap" design="GlobalIcoText1">
              Used in {campaigns.length} {campaigns.length === 1 ? 'campaign' : 'campaigns'}
            </Text>
            <Text
              inheritButtonHover
              color={`var(--global-ico-text-${open ? 1 : 3})`}
              hoverColor="var(--global-ico-text-1)"
            >
              <RotatableIcon name={ICONS.KEYBOARD_ARROW_DOWN} size={12} rotate={open && !closing ? '180deg' : '0'} />
            </Text>
          </Button>
        )}
      >
        {({ handleClose, triggerBounds }) => {
          // opens centered below the trigger, kept inside the window
          const top = (triggerBounds?.bottom ?? 0) + TRIGGER_GAP;
          const triggerCenter = (triggerBounds?.left ?? 0) + (triggerBounds?.width ?? 0) / 2;
          const left = Math.max(
            WINDOW_EDGE,
            Math.min(triggerCenter - PANEL_WIDTH / 2, globalWindowSize.innerWidth - PANEL_WIDTH - WINDOW_EDGE)
          );
          const maxHeight = Math.min(PANEL_MAX_HEIGHT, globalWindowSize.innerHeight - top - WINDOW_EDGE);
          return (
            <Container
              className={`flex fd-column gap-12 padding-16 ${classes.panel}`}
              // set through the prop so Container's own :hover background matches instead of going transparent
              background="var(--gray-900)"
              style={{ top, left, width: PANEL_WIDTH, maxHeight }}
            >
              <Container className="flex ai-center jc-space-between gap-16">
                <Text className="C1" design="GlobalIcoText3" transform="uppercase" clamp={1}>
                  Used in {projectName ?? 'this event'}
                </Text>
                <Button clear onClick={handleClose} aria-label="Close">
                  <Icon name={ICONS.CLOSE} size={12} color="var(--global-ico-text-2)" />
                </Button>
              </Container>
              <Container className="flex fd-column gap-8">
                {campaigns.map((campaign) => {
                  const statusColor = getCampaignStatusColor(campaign.state);
                  return (
                    <Link
                      key={campaign.uuid}
                      className={classes.campaignRow}
                      to={`/project/edit/${projectUuid}/${language}/campaigns/${campaign.uuid}/edit`}
                      onClick={handleClose}
                    >
                      <Icon name={ICONS.EMAIL} size={14} color="var(--global-ico-text-2)" />
                      <Text className="S2" design="GlobalIcoText1" clamp={1}>
                        {campaign.title}
                      </Text>
                      <Pill
                        className={classes.statusPill}
                        colorMix={{ color: statusColor.color, mix: statusColor.mix }}
                        padding="2px 8px"
                        borderRadius="8px"
                      >
                        <Text className="C2" color={statusColor.color} whiteSpace="nowrap">
                          {getCampaignStatusLabel(campaign.state)}
                        </Text>
                      </Pill>
                    </Link>
                  );
                })}
              </Container>
            </Container>
          );
        }}
      </Popover>
    </>
  );
};

export default CampaignUsageDropdown;
